import React from 'react';
import { Link } from 'react-router-dom';
import { useWishlist } from '../context/WishlistContext';
import { useToast } from '../context/ToastContext';
import ProductImage from './ProductImage';
import EmptyState from './EmptyState';

function MiniWishlist({ onItemClick, onClose, showHeader = true }) {
  const { wishlist, toggleWishlist, moveWishlistToCart, loading } = useWishlist();
  const { showError, showSuccess } = useToast();

  const handleRemove = async (e, productId) => {
    e.preventDefault();
    e.stopPropagation();
    const result = await toggleWishlist(productId);
    if (result && result.success === false) {
      showError(result.message || 'Failed to update wishlist');
    }
  };

  const handleItemClick = (productId) => {
    if (onItemClick) {
      onItemClick();
    }
  };

  if (wishlist.items.length === 0) {
    return (
      <div className="mini-wishlist-empty">
        <EmptyState
          variant="wishlist"
          title="Wishlist is Empty"
          message="Save items you love for later"
          actionLabel="Browse Products"
          actionLink="/products"
          className="mini-wishlist-empty-state"
        />
      </div>
    );
  }

  return (
    <div className="mini-wishlist">
      {showHeader && (
        <div className="mini-wishlist-header">
          <span className="mini-wishlist-count">{wishlist.count} {wishlist.count === 1 ? 'item' : 'items'}</span>
          {onClose && (
            <button
              className="mini-wishlist-close"
              onClick={onClose}
              aria-label="Close wishlist"
              type="button"
            >
              <i className="bi bi-x-lg" />
            </button>
          )}
        </div>
      )}
      <div className="mini-wishlist-items">
        {wishlist.items.slice(0, 5).map((item) => (
          <Link
            key={item.productId}
            to={`/product/${item.productId}`}
            className="mini-wishlist-item"
            onClick={() => handleItemClick(item.productId)}
            role="menuitem"
          >
            <ProductImage
              src={item.image}
              alt={item.name}
              className="mini-wishlist-item-image"
            />
            <div className="mini-wishlist-item-details">
              <div className="mini-wishlist-item-name">{item.name}</div>
              <div className="mini-wishlist-item-price">£{item.price.toFixed(2)}</div>
            </div>
            <button
              className="mini-wishlist-item-remove"
              onClick={(e) => handleRemove(e, item.productId)}
              disabled={loading}
              aria-label={`Remove ${item.name} from wishlist`}
            >
              <i className="bi bi-x-lg" />
            </button>

            <button
              className="mini-wishlist-item-add"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                moveWishlistToCart(item.productId, 1).then((result) => {
                  if (!result?.success) showError(result?.message || 'Failed to move item');
                  else showSuccess('Moved to cart');
                });
              }}
              disabled={loading || item.stockStatus === 'out_of_stock'}
              aria-label={`Move ${item.name} to cart`}
              title={item.stockStatus === 'out_of_stock' ? 'Out of stock' : 'Move to cart'}
              type="button"
            >
              <i className="bi bi-cart-plus" />
            </button>
          </Link>
        ))}
        {wishlist.items.length > 5 && (
          <div className="mini-wishlist-more text-muted small text-center py-2">
            +{wishlist.items.length - 5} more items
          </div>
        )}
      </div>
      <div className="mini-wishlist-footer">
        <Link to="/wishlist" className="btn btn-sm btn-outline-primary w-100" onClick={onItemClick}>
          View Full Wishlist
        </Link>
      </div>
    </div>
  );
}

export default MiniWishlist;
